import { fetchMetric, MetricFetchOutcome } from 'api/customerInsights';
import { PREVIOUS_END_DATE_KEY, PREVIOUS_START_DATE_KEY } from 'api/metricQuery';

import { CardMetricScope, isProductReadinessIdentifier } from '../registry/metricIdentifiers';
import { assembleCard, CardAssembly, identifiersForCard } from './assembleCard';

// The live source behind the Customer Insights seam. There is no switch: the API is the only source
// the screen has.
//
// WHAT THIS CURRENTLY RETURNS, measured 2026-08-20 and reproduced independently by two people with
// separate tokens. This is upstream behaviour, not a defect of this file, and it is what the page
// shows today:
//
//   UAT (`api-uat.perxtech.io`, tenant `generic`) — every All-industry metric returns HTTP 502
//   `50201`. 13 identifiers tried, all identical. The route, the auth, the catalogue lookup and the
//   industry gating all work: a Banking metric on a non-Banking tenant and a nonexistent metric both
//   correctly return 403, which is what proves the probe discriminates. The failure is at the
//   Metabase step, the CAUSE is unknown, and only the backend's logs can name it.
//
//   Production — both question-id files are empty, so every metric returns 404 `40415`, which is the
//   contract's designed not-deployed signal rather than an error.
//
// So every card renders a data state rather than a reading, on both environments, until that is
// fixed upstream. It degrades rather than crashing, and it never falls back to a fixture: a fixture
// number appearing because the API failed would be indistinguishable from a measurement.
// PLA-8815, plan `metabase-data-seam.md` § VI.

/**
 * The comparison period, dropped.
 *
 * Product Readiness is the one metric family that REJECTS it. Probed on UAT 2026-09-18:
 * `O-BFS-PR-001` answers 200 with `start_date`/`end_date` and 502 (`50201`) the moment
 * `previous_start_date` and `previous_end_date` are added, while `O-COM-001` answers identically
 * either way. Nothing on the readiness path reads a previous period —
 * `sections/productReadinessLive.ts`, `data/productReadinessMetric.ts` and
 * `data/productReadinessResult.ts` have no delta branch — so the pair is dropped rather than
 * the card degraded. PLA-8815.
 */
const COMPARISON_KEYS: string[] = [PREVIOUS_START_DATE_KEY, PREVIOUS_END_DATE_KEY];

const withoutComparisonPeriod = (query: Record<string, string>): Record<string, string> =>
  Object.keys(query)
    .filter(key => COMPARISON_KEYS.indexOf(key) === -1)
    .reduce((narrowed, key) => ({ ...narrowed, [key]: query[key] }), {});

/**
 * One card's payload, fetched.
 *
 * The contract's own model is one call per metric — perx-api knows nothing about which metrics
 * compose which card — so a card is N concurrent requests reassembled here. They are issued together
 * rather than in sequence: a Banking tenant loads 69 metrics per page view, and serialising them
 * inside each card would multiply that by the card's tile count in wall-clock terms.
 *
 * Every request is resolved into an outcome rather than thrown (`fetchMetric` never rejects), so one
 * metric failing degrades its own tiles and leaves the card's other readings alone. A card is not
 * all-or-nothing.
 *
 * `scope` is the render-time parameter set that decides WHICH metric a card reads — Product
 * Readiness' selected product, and nothing else today. It reaches the fetch AND the assembly from
 * the same argument, so the ids requested can never be a different product's from the ids the
 * payload is read back under.
 */
export const fetchCardLive = async (
  cardKey: string,
  query?: Record<string, string>,
  scope?: CardMetricScope,
  // Passed straight through to the assembly for the demo layer's tenant check; the fetch itself is
  // already tenant-scoped by the token. `data/useCardData.ts` reads it from `useTenant()`.
  tenantName?: string | null
): Promise<CardAssembly> => {
  const identifiers = identifiersForCard(cardKey, scope);

  const outcomes = await Promise.all(
    identifiers.map(identifier =>
      fetchMetric({
        identifier,
        query:
          query && isProductReadinessIdentifier(identifier) ? withoutComparisonPeriod(query) : query
      })
    )
  );

  const byIdentifier = identifiers.reduce(
    (acc, identifier, index) => ({ ...acc, [identifier]: outcomes[index] }),
    {} as Record<string, MetricFetchOutcome>
  );

  // `'customer_insights'` is PLA-8815's interim card-confidence opt-in — see
  // `data/customerInsightsCardConfidence.ts`, which the backend owns and has not shipped. Naming
  // the layer rather than passing `true` is what keeps Customer 360's four same-named cards out of
  // this table; nothing else on this branch passes it.
  return assembleCard(cardKey, byIdentifier, scope, 'customer_insights', undefined, tenantName);
};

export default fetchCardLive;
